import { ethers } from 'ethers';

import config from 'config';
import { ChainConfig } from 'config/configType';
import { KATANA_PERPS, KATANA_DESC, urls } from 'config/constants';
import { waitAsync } from 'lib/promiseTools';
import { isRunningOnInsecureHttp } from 'lib/redirect';
import { staticDispatch } from 'reduxState/store';
import {
  changeWalletChainId,
  showWalletConnectQR,
  updatePendingWalletAddress,
} from 'reduxState/wallet/walletDetailsReducer';

import { getAnonymousRPCProvider } from './utils';
import { WalletConnectSigner } from './walletConnectV2Signer';

// Only import type so Code-split will work correctly
import type EthereumProviderType from '@walletconnect/ethereum-provider';
import type { EthereumProviderOptions } from '@walletconnect/ethereum-provider';

// (window as any).switchChain = async () => {
//   const walletAddress = (await getStateOutsideOfComponent()).wallet.walletAddress;
//   console.log('ORIGINAL:', getAddress(walletAddress));
//   const wcProvider = await initializeWalletConnect();
//   const chainId = config.chains.beraChain.chainId;
//   const chainHex = toQuantity(chainId);
//   console.log({ chainId, chainHex, provider: wcProvider.chainId });
//   await wcProvider.request({
//     method: 'wallet_switchEthereumChain',
//     params: [{ chainId: chainHex }],
//   });
// };

/**
 * Examples
 *  https://web3modal.com/
 *  https://react-dapp-v2-with-ethers.vercel.app/
 *
 * Supported Walllets V2
 *  https://explorer.walletconnect.com/?type=wallet&version=2
 *
 * Official example 2024
 *  https://github.com/WalletConnect/web-examples/blob/main/dapps/ethereum-provider/src/App.tsx#L25C49-L25C61
 *
 * Ethereum Provider (unused)
 *  https://docs.walletconnect.com/2.0/javascript/providers/ethereum
 *  https://github.com/WalletConnect/walletconnect-monorepo/tree/v2.0/providers/ethereum-provider
 *
 * Universal Provider & Ethers.js example (used)
 *  https://docs.walletconnect.com/2.0/javascript/providers/universal
 *  https://github.com/WalletConnect/web-examples/tree/main/dapps/react-dapp-v2-with-ethers
 *  https://github.com/WalletConnect/web-examples/blob/main/dapps/react-dapp-v2-with-ethers/src/contexts/ClientContext.tsx
 *
 * Another integration
 *  https://medium.com/walletconnect/walletconnect-sign-v2-0-beginners-guide-for-javascript-developers-c02c02d215c9
 *
 * Issues with ethers
 *  https://github.com/ethers-io/ethers.js/issues/3643
 *  https://github.com/WalletConnect/walletconnect-monorepo/issues/1871
 *  https://github.com/WalletConnect/walletconnect-monorepo/issues/1772
 *  https://github.com/WalletConnect/walletconnect-monorepo/issues/1981 (reported)
 *
 */
let provider: EthereumProviderType | undefined;

// monitor@idex.io https://dashboard.reown.com (1pass) 2025-12+
const projectId = '3ceeca7689ed6e296ec02779374413ed';

let EthereumProviderImported: typeof EthereumProviderType | undefined;

const importProviderSingleton = async (): Promise<
  typeof EthereumProviderType
> => {
  if (EthereumProviderImported) {
    return EthereumProviderImported;
  }
  EthereumProviderImported = (await import('@walletconnect/ethereum-provider'))
    .EthereumProvider;
  return EthereumProviderImported;
};

const wcConfig: EthereumProviderOptions = {
  projectId,
  // Cannot use chains directly as user might not have Katana in MetaMask and it would fail
  //  we have to enforce adding it
  // chains: [activeChain.chainId],
  // We will enforce later
  optionalChains: [
    // TODO: we need all chains we support for bridging, see useAllDepositWithdrawChains
    config.chains.katanaChain.chainId,
    // config.chains.xchainEVM.chainId,
    // config.chains.beraChain.chainId,
    // Always keep ETH cause user might not have custom chains initially
    config.chains.ethereum.chainId,
  ],
  // optionalMethods: [/*'eth_signTypedData',*/ 'eth_signTypedData_v4', /* 'eth_sign', */],
  rpcMap: {
    // Official RPC `https://rpc.walletconnect.com?chainId=eip155:${chainId}&projectId=${projectId}`
    // [chain.chainId]: chain.chainRpc,
    // TODO: we need all chains we support for bridging
    [config.chains.katanaChain.chainId]: config.chains.katanaChain.chainRpc,
    // [config.chains.xchainEVM.chainId]: config.chains.xchainEVM.chainRpc,
    // [config.chains.beraChain.chainId]: config.chains.beraChain.chainRpc,
    [config.chains.ethereum.chainId]: config.chains.ethereum.chainRpc,
  },
  showQrModal: false,
  // methods, // OPTIONAL ethereum methods
  // events, // OPTIONAL ethereum events
  metadata: {
    name: KATANA_PERPS,
    description: KATANA_DESC,
    url: document.location.origin || urls.exchange,
    // Warning: Missing image could lead to in-wallet errors (eg. in MetaMask)
    icons: [`${urls.exchange}/static/images/walletConnect-katana.png`],
  },
};

// Se also TrustWallet docs https://developer.trustwallet.com/wallet-connect/dapp
const runWalletConnectInitialize = async (): Promise<EthereumProviderType> => {
  if (isRunningOnInsecureHttp()) {
    throw new Error('WalletConnect is only working on secure (https) domains');
  }
  if (provider?.connected) {
    console.log('[WalletConnect] Using already connected');
    // await provider.disconnect();
    await provider.enable();
    //const result = await provider.request({ method: 'eth_requestAccounts' });
    // console.log('[WalletConnect] CONNECTd', result);

    // provider.signer.setDefaultChain(
    //   `eip155:${config.chains.beraChain.chainId}`,
    //   config.chains.beraChain.chainRpc
    // );
    // const activeChain = getCurrentStateChainConfig();
    // if (Number(provider.chainId) !== Number(activeChain.chainId)) {
    //   await triggerWalletConnectChainSwitchIfPossible(activeChain, provider);
    //   throwOnDifferentThanCurrentChainId(provider.chainId);
    // }
    return provider;
  }

  if (provider && !provider.connected) {
    // Instead of this we trigger chain change instead before signing
    // provider.signer.setDefaultChain(
    //   `eip155:${config.chains.xchainEVM.chainId}`,
    //   config.chains.xchainEVM.chainRpc
    // );

    await provider.enable();
    return provider;
  }
  const EthereumProvider = await importProviderSingleton();

  provider = provider ?? (await EthereumProvider.init(wcConfig));
  provider.on('connect', provider => {
    console.log('[WalletConnect] Connected', provider);
    staticDispatch(changeWalletChainId(Number(provider.chainId)));
    // TODO: Still works ????
    // const wallet = (provider as any)?.accounts?.[0];
    // console.log('!!!!!!!!!!!!!! CONNECTED !', { wallet });
    // staticDispatch(updatePendingWalletAddress(wallet));
  });
  provider.on('display_uri', displayUri => {
    console.log('display_uri', displayUri);
    staticDispatch(showWalletConnectQR(displayUri));
  });
  provider.on('chainChanged', chain => {
    console.log('[WalletConnect] chainChanged', chain);
    staticDispatch(changeWalletChainId(Number(chain)));
  });
  provider.on('accountsChanged', accounts => {
    const firstAcc = accounts[0];
    staticDispatch(updatePendingWalletAddress(firstAcc));
  });
  provider.on('session_update', session => {
    console.log('[WalletConnect] Session update (unhandled)', session);
    // staticDispatch(changeWalletChainId(session.id));
  });
  provider.on('disconnect', providerError => {
    console.log('[WalletConnect] Disconnected', providerError);
    provider = undefined;
  });

  console.log('[WalletConnect] Enabling...');
  await provider.enable();

  // try {
  //   throwOnDifferentThanCurrentChainId(provider.chainId);
  // } catch (error) {
  //   console.warn('[WalletConnect] Killing existing session, chain switched');
  //   await provider.disconnect().catch(console.warn);
  //   provider = undefined;
  //   // This is a bit edge case, so better to communicate with user
  //   // and fail current request to prevent any weird behaviour or reconnect loop.
  //   throw new Error(
  //     `${KUMA} is running on different chain than existing WalletConnect session. WalletConnect session will be terminated. Please retry action now.`
  //   );
  // }
  return provider;
};

let initializationPromise: Promise<EthereumProviderType> | undefined;

/**
 * Call initialization call. This call will internally only run once
 *  - each other parallel call will return same Promise.
 */
export const initializeWalletConnect =
  async (): Promise<EthereumProviderType> => {
    if (initializationPromise) {
      return initializationPromise;
    }
    initializationPromise = runWalletConnectInitialize();
    try {
      const connector = await initializationPromise;
      return connector;
    } finally {
      staticDispatch(showWalletConnectQR(undefined));

      initializationPromise = undefined;
    }
  };

export const unlockWalletConnect = async (): Promise<void> => {
  if (provider) {
    console.log('[WalletConnect] Disconnecting existing provider...');
    await Promise.race([
      provider
        ?.disconnect()
        .then(() => {
          console.log('[WalletConnect] Disconnected');
        })
        .catch(error => {
          console.warn(error);
          console.warn(
            '[WalletConnect] Provider disconnect failed - continuing'
          );
        }),
      waitAsync(5000).then(() =>
        console.warn('[WalletConnect] Provider disconnect timeout - continuing')
      ),
    ]);
    // provider = undefined;
  } else {
    // Disconnecting Wallet here cause weird issues with next connect connect
    //  It freeze on connect call.
    //  For user to disconnect existing session, it's required for user to
    console.log(
      '[walletConnect] Ignoring disconnect (provider not initialized)'
    );
    // console.log(
    //   '[walletConnect] Creating temp wallet Connect - for proper disconnect'
    // );
    // const EthereumProvider = await importProviderSingleton();
    // // // Create it just so we can kill it properly
    // provider = await EthereumProvider.init(wcConfig);
    // await waitAsync(2000);
    // console.log({
    //   connected: provider.connected,
    //   connecting: provider.connecting,
    // });
    // try {
    //   await provider.disconnect();
    //   // provider.session?.acknowledged
    // } catch (error) {
    //   console.warn(error);
    // }
    // console.log('[walletConnect] Disconnected wallet');
    // // provider = undefined;
    // await waitAsync(2_000);
  }
  // ?? provider = undefined;

  // Clean existing wallets stored in Local storage ???
  // removeWalletConnectFromLocalStorage();

  // Just safety wait to be sure WalletConnect had time to cleanup
  waitAsync(200);

  await initializeWalletConnect();
};

export const getWalletConnectWallet = async (): Promise<
  { publicKey: string } | undefined
> => {
  const connector = await initializeWalletConnect();
  return connector.accounts[0]
    ? { publicKey: connector.accounts[0] }
    : undefined;
};

export const removeWalletConnectFromLocalStorage = () => {
  console.log('[WalletConnect] Clearing up localstorage');
  provider = undefined;
  Object.keys(window.localStorage)
    .filter(
      key =>
        key.includes('walletconnect') ||
        key.includes('-walletlink:') ||
        key.includes('wc@2:')
    )
    .map(keyToRemove => localStorage.removeItem(keyToRemove));
};

// export const resetWalletConnect = async () => {
//   if (!provider) {
//     return;
//   }
//   provider?.events.removeAllListeners();
//   provider = undefined;
//   return runWalletConnectInitialize();
// };

export const getSigner = async (
  walletAddress: string,
  enforcedChain: ChainConfig | undefined
): Promise<ethers.JsonRpcSigner> => {
  // Note that currently, anonymous provider is not used, because we dispatch by wallet directly
  console.log('Getting WalletConnect signer', enforcedChain?.chainNetwork);
  const provider = getAnonymousRPCProvider(enforcedChain);
  return new WalletConnectSigner(provider, walletAddress, true);
};
